import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { 
  Radio,
  Plus,
  Play,
  Users,
  DollarSign,
  Calendar,
  Clock,
  Eye,
  Download,
  Share2,
  Settings,
  TrendingUp,
  Zap,
  Heart,
  Star,
  BarChart3,
  Video,
  CheckCircle2,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const LiveStreamManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Mock data - replace with real data from your backend
  const revenueStats = {
    totalEarnings: 2450,
    thisMonth: 850,
    pendingPayout: 420,
    avgPerEvent: 245
  };

  const upcomingEvents = [
    {
      id: '1',
      title: 'Spring Recital 2024',
      date: '2024-03-15',
      time: '7:00 PM',
      expectedViewers: 45,
      projectedRevenue: 540,
      status: 'scheduled'
    },
    {
      id: '2', 
      title: 'Level 3 Competition',
      date: '2024-03-22',
      time: '2:00 PM',
      expectedViewers: 28,
      projectedRevenue: 336,
      status: 'draft'
    }
  ];

  const recentEvents = [
    {
      id: '3',
      title: 'Winter Showcase',
      date: '2024-02-28',
      viewers: 52,
      revenue: 624,
      yourEarnings: 312,
      status: 'completed',
      recording: true
    },
    {
      id: '4',
      title: 'Gymnastics Meet #1',
      date: '2024-02-15', 
      viewers: 31,
      revenue: 372,
      yourEarnings: 186,
      status: 'completed',
      recording: true
    }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Revenue Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Earnings</p>
                <p className="text-3xl font-bold text-green-900">${revenueStats.totalEarnings}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">This Month</p>
                <p className="text-3xl font-bold text-blue-900">${revenueStats.thisMonth}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Pending Payout</p>
                <p className="text-3xl font-bold text-purple-900">${revenueStats.pendingPayout}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Avg Per Event</p>
                <p className="text-3xl font-bold text-orange-900">${revenueStats.avgPerEvent}</p>
              </div>
              <div className="bg-orange-100 rounded-full p-3">
                <BarChart3 className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Share Highlight */}
      <Card className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2 flex items-center">
                <Sparkles className="h-6 w-6 mr-2" />
                You Keep 50% of All Revenue
              </h3>
              <p className="text-green-100 text-lg">
                Every ticket sold, every event streamed—automatically deposited to your account within 2 business days.
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">${revenueStats.totalEarnings}</div>
              <div className="text-green-200">Total Earned</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <Plus className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Create New Live Event</h3>
                <p className="text-gray-600">Set up your next live streaming event in minutes</p>
              </div>
            </div>
            <Button className="w-full bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Event
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow duration-300">
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-full p-3 mr-4">
                <Play className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Go Live Now</h3>
                <p className="text-gray-600">Start an instant stream for any unplanned event</p>
              </div>
            </div>
            <Button variant="outline" className="w-full border-green-600 text-green-600 hover:bg-green-50">
              <Radio className="h-4 w-4 mr-2" />
              Go Live
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderUpcomingTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900">Upcoming Events</h3>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Event
        </Button>
      </div>

      <div className="grid gap-6">
        {upcomingEvents.map((event) => (
          <Card key={event.id} className="hover:shadow-lg transition-shadow duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{event.title}</h4>
                    <p className="text-gray-600">{event.date} at {event.time}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-1" />
                        {event.expectedViewers} expected viewers
                      </span>
                      <span className="flex items-center text-sm text-green-600 font-medium">
                        <DollarSign className="h-4 w-4 mr-1" />
                        ${event.projectedRevenue} projected revenue
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge 
                    variant={event.status === 'scheduled' ? 'default' : 'secondary'}
                    className={event.status === 'scheduled' ? 'bg-green-100 text-green-800' : ''}
                  >
                    {event.status}
                  </Badge>
                  <Button size="sm">
                    <Settings className="h-4 w-4 mr-2" />
                    Manage
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderRecentTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Recent Events</h3>
      
      <div className="grid gap-6">
        {recentEvents.map((event) => (
          <Card key={event.id} className="hover:shadow-lg transition-shadow duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-100 rounded-full p-3">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{event.title}</h4>
                    <p className="text-gray-600">{event.date}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="flex items-center text-sm text-gray-500">
                        <Eye className="h-4 w-4 mr-1" />
                        {event.viewers} viewers
                      </span>
                      <span className="flex items-center text-sm text-blue-600">
                        <DollarSign className="h-4 w-4 mr-1" />
                        ${event.revenue} total revenue
                      </span>
                      <span className="flex items-center text-sm text-green-600 font-bold">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        ${event.yourEarnings} your earnings
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {event.recording && (
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  )}
                  <Button variant="outline" size="sm">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'upcoming', label: 'Upcoming Events', icon: Calendar },
    { id: 'recent', label: 'Recent Events', icon: CheckCircle2 }
  ];

  return (
    <div className="space-y-8">
      <StandardPageHeader
        title="Live Stream Management"
        subtitle="Create live streaming events and earn 50% of all ticket revenue"
        icon={Radio}
        actions={
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Live Event
          </Button>
        }
      />

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'upcoming' && renderUpcomingTab()}
        {activeTab === 'recent' && renderRecentTab()}
      </motion.div>

      {/* Bottom CTA */}
      <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <CardContent className="p-8 text-center">
          <h3 className="text-2xl font-bold mb-4 flex items-center justify-center">
            <Heart className="h-6 w-6 mr-2" />
            Ready to Create Your Next Revenue Stream?
          </h3>
          <p className="text-blue-100 mb-6 text-lg">
            Set up a live event in under 10 minutes and start earning 50% of every ticket sold.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-bold"
          >
            <Zap className="h-5 w-5 mr-2" />
            Create Your First Live Event
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveStreamManagement;